import useQueryCampaigns from '../../../hooks/useQueryCampaigns';
import { useState } from 'react';
import { useAddNotification } from '@/modules/notifications/store';
import { TFunction } from 'i18next';
import { NotificationVariants, SubPage } from '@/common/components';
import { CampaignTrackingContent } from './CampaignTrackingContent';
import useQueryCampaignTrackingOverviewData from '@/modules/study-insights/hooks/useQueryCampaignTrackingOverview';
import { AddCampaignModal } from './components/AddCampaignModal';
import { CampaignsPillId } from '@/modules/study-insights/types';
import { CAMPAIGN_STATUS } from '@/modules/study-insights/constants';
import { GroupKey } from '@/modules/study-insights/types/api';
import { DateGroupOptionId } from './components/CampaignOverview/components/StackedBarChart';

const ID = 'campaign-tracking-content';

const mapDateGroupOptionToGroupKey = (value: DateGroupOptionId): GroupKey => {
  switch (value) {
    case DateGroupOptionId.DAILY:
      return GroupKey.BY_DAY;
    case DateGroupOptionId.WEEKLY:
    default:
      return GroupKey.BY_WEEK;
  }
};

interface CampaignTrackingProps {
  t: TFunction;
}

export const CampaignTracking = ({ t }: CampaignTrackingProps): JSX.Element => {
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [selectedPill, setSelectedPill] = useState<CampaignsPillId>(CampaignsPillId.ACTIVE_CAMPAIGNS);
  const [overviewDateOption, setOverviewDateOption] = useState<DateGroupOptionId>(DateGroupOptionId.WEEKLY);

  const addNotification = useAddNotification();

  const {
    isLoading: isLoadingCampaignTrackingOverview,
    isRefetching: isRefetchingCampaignTrackingOverview,
    data: campaignTrackingOverviewData,
    refetch: refetchCampaignOverview,
  } = useQueryCampaignTrackingOverviewData({
    groupKey: mapDateGroupOptionToGroupKey(overviewDateOption),
  });

  const {
    isLoading: isLoadingCampaignsData,
    isRefetching: isRefetchingCampaignsData,
    data: campaignList = [],
    refetch: refetchCampaigns,
  } = useQueryCampaigns({
    status: selectedPill === CampaignsPillId.ACTIVE_CAMPAIGNS ? CAMPAIGN_STATUS.ACTIVE : CAMPAIGN_STATUS.INACTIVE,
  });

  const onCreateCampaign = async (): Promise<void> => {
    setShowCreateCampaignModal(false);
    addNotification({
      title: t('add_campaign.success_notification'),
      type: NotificationVariants.SUCCESS,
      autoClose: true,
    });
    await refetchCampaignOverview();
    await refetchCampaigns();
  };

  const onEditCampaign = async (): Promise<void> => {
    await refetchCampaignOverview();
    await refetchCampaigns();
  };

  const onCreateCampaignError = (): void => {
    addNotification({
      title: t('add_campaign.error_notification'),
      type: NotificationVariants.ERROR,
      autoClose: true,
    });
  };

  const handleOpenCreateCampaignModal = (): void => {
    setShowCreateCampaignModal(true);
  };

  const handleDateGroupOptionChange = (value: DateGroupOptionId): void => {
    setOverviewDateOption(value);
  };

  return (
    <SubPage id={ID} data-testid={ID} hasSeparator={false}>
      <CampaignTrackingContent
        id={ID}
        isLoadingCampaignList={isLoadingCampaignsData || isRefetchingCampaignsData}
        isLoadingCampaignTrackingOverview={isLoadingCampaignTrackingOverview || isRefetchingCampaignTrackingOverview}
        campaignTrackingOverviewData={campaignTrackingOverviewData}
        campaigns={campaignList}
        onOpenCreateCampaignModal={handleOpenCreateCampaignModal}
        onSuccessfulEditCampaign={onEditCampaign}
        onDateGroupOptionChange={handleDateGroupOptionChange}
        onSelectPill={setSelectedPill}
        selectedPill={selectedPill}
        selectedDateGroupOption={overviewDateOption}
      />
      <AddCampaignModal
        show={showCreateCampaignModal}
        onClose={() => setShowCreateCampaignModal(false)}
        onCreateCampaign={onCreateCampaign}
        onErrorCreateCampaign={onCreateCampaignError}
        modalTitle={t('add_campaign.modal_title')}
      />
    </SubPage>
  );
};
